<template>
  <div>
    <div class="dashboard__payment-history">
      <div class="d-flex">
        <div class="ml-auto mb-3 form__input dashboard__search">
          <v-date-picker
            mode="range"
            v-model="dateRange"
            :popover="{
              placement: $screens({ default: 'bottom', md: 'bottom-end' })
            }"
            :columns="$screens({ default: 1, lg: 2 })"
            :input-props="{
              class: '',
              placeholder: 'Last 30 days',
              readonly: true
            }"
            :min-date="startDate"
            :max-date="new Date()"
            :select-attribute="selectDragAttribute"
            :drag-attribute="selectDragAttribute"
            @drag="dragValue = $event"
          >
            <div slot="day-popover" slot-scope="{ format }">
              {{
                format(dragValue ? dragValue.start : dateRange.start, "MMM D")
              }}
              -
              {{ format(dragValue ? dragValue.end : dateRange.end, "MMM D") }}
            </div>
          </v-date-picker>
        </div>
        <download-csv
          class="alt_btn ml-3 export__btn"
          :data="exportData"
          :name="`Invoices for ${invoiceName}.csv`"
          :disable="exportLoader"
        >
          <svgicon
            v-if="exportLoader"
            icon="spinner"
            class="button__loader"
          ></svgicon>
          Export
        </download-csv>
      </div>

      <div class="dashboard__payment-history-wrap">
        <table class="dashboard__payment-history-table">
          <thead>
            <tr>
              <th class="dashboard__payment-history-table-heading">
                Member name
              </th>
              <th class="dashboard__payment-history-table-heading">
                Space name
              </th>
              <th class="dashboard__payment-history-table-heading">
                Sub type
              </th>
              <th class="dashboard__payment-history-table-heading">
                Space type
              </th>
              <th class="dashboard__payment-history-table-heading">
                Amount
              </th>
              <th class="dashboard__payment-history-table-heading">
                Date
              </th>
              <th class="dashboard__payment-history-table-heading">
                Invoice
              </th>
              <th class="dashboard__payment-history-table-heading"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(paymentHistory, index) in paymentHistories"
              :key="index"
            >
              <td class="dashboard__payment-history-table-data">
                {{ paymentHistory.memberFullName }}
              </td>
              <td class="dashboard__payment-history-table-data">
                {{ paymentHistory.spaceName }}
              </td>
              <td class="dashboard__payment-history-table-data">
                {{ paymentHistory.subPlan | capitalize }}
              </td>
              <td class="dashboard__payment-history-table-data">
                {{ paymentHistory.spaceType | capitalize }}
              </td>
              <td class="dashboard__payment-history-table-data">
                {{ paymentHistory.currency === "GHS" ? "¢" : "₦" }}
                {{ new Intl.NumberFormat().format(paymentHistory.amount) }}
              </td>
              <td class="dashboard__payment-history-table-data">
                {{ formatDate(paymentHistory.paymentDate) }}
              </td>
              <td class="dashboard__payment-history-table-data">
                <svgicon
                  icon="view"
                  class="dashboard__payment-history-resp-download-icon"
                  style="cursor: pointer;"
                  @click="
                    toggleInvoice(
                      paymentHistory.invoiceId,
                      paymentHistory.subPlan
                    )
                  "
                ></svgicon>
              </td>
              <td class="dashboard__payment-history-table-data">
                <svgicon
                  @click="generateReport(paymentHistory)"
                  icon="download"
                  style="cursor: pointer;"
                  class="dashboard__payment-history-resp-download-icon"
                ></svgicon>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="dashboard__payment-history-resp"
        v-for="(paymentHistory, index) in paymentHistories"
        :key="index"
      >
        <div class="dashboard__payment-history-resp-details">
          <p class="dashboard__payment-history-resp-name">
            {{ paymentHistory.memberFullName }}
          </p>
          <p class="dashboard__payment-history-resp-others">
            {{ paymentHistory.subPlan | capitalize }} |
            {{ paymentHistory.spaceType | capitalize }}
          </p>
        </div>
        <div class="dashboard__payment-history-resp-details">
          <p class="dashboard__payment-history-resp-name">
            {{ paymentHistory.spaceName }}
          </p>

          <p class="dashboard__payment-history-resp-others">
            {{ formatDate(paymentHistory.paymentDate) }}
          </p>
        </div>
        <div class="dashboard__payment-history-resp-details">
          <p class="dashboard__payment-history-resp-name">
            ₦
            {{ new Intl.NumberFormat().format(paymentHistory.amount) }}
          </p>
          <div
            @click="generateReport(paymentHistory)"
            class="dashboard__payment-history-resp-download"
          >
            <span class="dashboard__payment-history-resp-download-text"
              >Download receipt</span
            >
            <svgicon
              icon="download"
              class="dashboard__payment-history-resp-download-icon"
            ></svgicon>
          </div>
        </div>
      </div>

      <div class="dashboard__pagination">
        <b-pagination
          v-if="paymentHistories.length > 0"
          pills
          v-model="currentPage"
          :total-rows="count"
          :per-page="pageSize"
          prev-text="Prev"
          next-text="Next"
          first-number
          last-number
          @change="handlePagination"
        ></b-pagination>
      </div>
      <div
        v-show="paymentHistories.length < 1"
        class="dashboard__payment-history-table-empty"
      >
        No Transaction History Found
      </div>

      <loader :loader="loader"></loader>

      <AppModal
        :show="showInvoice"
        @onclosed="showInvoice = false"
        :styleGotten="{ 'max-width': '700px', top: '4%' }"
        v-if="invoice"
      >
        <div class="invoice">
          <img
            src="../../assets/images/logo.svg"
            alt=""
            class="invoice__logo"
          />
          <div class="invoice__date-wrap">
            <p class="invoice__date-title">Invoice date</p>
            <p class="invoice__date">{{ invoice.dueDate | formatDate }}</p>
          </div>

          <div class="invoice__top">
            <div class="invoice__title-wrap">
              <p class="invoice__title">Invoice</p>
              <p class="invoice__for">
                {{ invoice.subPlan | capitalize }} Subscription
              </p>
            </div>
            <div class="invoice__date-wrap">
              <p class="invoice__date-title">Invoice Number</p>
              <p class="invoice__date">{{ invoice.invoiceNumber }}</p>
            </div>
          </div>

          <div class="invoice__action">
            <div class="invoice__to">
              <p class="invoice__to-title">Billed to</p>
              <p class="invoice__to-name renter_name">{{ invoice.fullname }}</p>
              <p class="invoice__to-address">
                {{ invoice.fullAddress }}
              </p>
            </div>
          </div>

          <span
            class="invoice__status"
            v-if="invoice.paymentStatus === 'success'"
            >Paid</span
          >

          <div class="dashboard__payment-history-wrap invoice__table">
            <table class="dashboard__payment-history-table">
              <thead>
                <tr>
                  <th class="dashboard__payment-history-table-heading">
                    Description
                  </th>
                  <th class="dashboard__payment-history-table-heading">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="dashboard__payment-history-table-data">
                    Rental Subscription
                  </td>

                  <td class="dashboard__payment-history-table-data">
                    {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                    {{
                      new Intl.NumberFormat().format(
                        Number(invoice.rent) + Number(invoice.commission)
                      )
                    }}
                  </td>
                </tr>
                <tr v-if="invoice.booking_fee > 0">
                  <td class="dashboard__payment-history-table-data">
                    Booking Fee
                  </td>

                  <td class="dashboard__payment-history-table-data">
                    {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                    {{
                      new Intl.NumberFormat().format(
                        Number(invoice.booking_fee)
                      )
                    }}
                  </td>
                </tr>
                <tr v-if="invoice.booking_fee > 0">
                  <td class="dashboard__payment-history-table-data">
                    Security Deposit
                  </td>

                  <td class="dashboard__payment-history-table-data">
                    {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                    {{
                      new Intl.NumberFormat().format(
                        Number(invoice.securityDeposit)
                      )
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="dashboard__payment-history-table-data">
                    Service Charge
                  </td>

                  <td class="dashboard__payment-history-table-data">
                    {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                    {{
                      new Intl.NumberFormat().format(
                        Number(invoice.service_charge)
                      )
                    }}
                  </td>
                </tr>
                <tr
                  v-for="(extraCharge, index) in extrachargeInvoice"
                  :key="index"
                >
                  <td class="dashboard__payment-history-table-data">
                    {{ extraCharge.extrachargeType }}
                  </td>

                  <td class="dashboard__payment-history-table-data">
                    {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                    {{ new Intl.NumberFormat().format(extraCharge.amount) }}
                  </td>
                </tr>
                <tr>
                  <td class="dashboard__payment-history-table-data">
                    Discount
                  </td>

                  <td class="dashboard__payment-history-table-data">
                    {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                    {{
                      new Intl.NumberFormat().format(Number(invoice.discount))
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="dashboard__payment-history-table-data">
                    VAT (7.5%)
                  </td>

                  <td class="dashboard__payment-history-table-data">
                    {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                    {{ new Intl.NumberFormat().format(Number(invoice.vat)) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="invoice__total-wrap">
            <p class="invoice__total-title">
              Amount Due {{ invoice.currency === "GHS" ? "GHC" : "NGN" }}
            </p>
            <p class="invoice__total">
              {{ new Intl.NumberFormat().format(invoice.total) }}
            </p>
          </div>
          <p class="invoice__due" v-if="invoice.next_renewal_date">
            Next Due Date: {{ invoice.next_renewal_date | formatDate }}
          </p>
        </div>
      </AppModal>

      <!-- download pdf -->
      <div class="invoice__hide">
        <div class="invoice__content" ref="invoicePDF">
          <div class="invoice" v-if="invoice">
            <img
              src="../../assets/images/logo.svg"
              alt=""
              class="invoice__logo"
            />
            <div class="invoice__date-wrap">
              <p class="invoice__date-title">Invoice date</p>
              <p class="invoice__date">{{ invoice.dueDate | formatDate }}</p>
            </div>

            <div class="invoice__top">
              <div class="invoice__title-wrap">
                <p class="invoice__title">Invoice</p>
                <p class="invoice__for">
                  {{ invoice.subPlan | capitalize }} Subscription
                </p>
              </div>
              <div class="invoice__date-wrap">
                <p class="invoice__date-title">Invoice Number</p>
                <p class="invoice__date">{{ invoice.invoiceNumber }}</p>
              </div>
            </div>

            <div class="invoice__action">
              <div class="invoice__to">
                <p class="invoice__to-title">Billed to</p>
                <p class="invoice__to-name renter_name">{{ invoice.fullname }}</p>
                <p class="invoice__to-address">
                  {{ invoice.fullAddress }}
                </p>
              </div>
            </div>

            <span
              class="invoice__status"
              v-if="invoice.paymentStatus === 'success'"
              >Paid</span
            >

            <div class="dashboard__payment-history-wrap invoice__table">
              <table class="dashboard__payment-history-table">
                <thead>
                  <tr>
                    <th class="dashboard__payment-history-table-heading">
                      Description
                    </th>
                    <th class="dashboard__payment-history-table-heading">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="dashboard__payment-history-table-data">
                      Rental Subscription
                    </td>

                    <td class="dashboard__payment-history-table-data">
                      {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                      {{
                        new Intl.NumberFormat().format(
                          Number(invoice.rent) + Number(invoice.commission)
                        )
                      }}
                    </td>
                  </tr>
                  <tr v-if="invoice.booking_fee > 0">
                    <td class="dashboard__payment-history-table-data">
                      Booking Fee
                    </td>

                    <td class="dashboard__payment-history-table-data">
                      {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                      {{
                        new Intl.NumberFormat().format(
                          Number(invoice.booking_fee)
                        )
                      }}
                    </td>
                  </tr>
                  <tr v-if="invoice.booking_fee > 0">
                    <td class="dashboard__payment-history-table-data">
                      Security Deposit
                    </td>

                    <td class="dashboard__payment-history-table-data">
                      {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                      {{
                        new Intl.NumberFormat().format(
                          Number(invoice.securityDeposit)
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td class="dashboard__payment-history-table-data">
                      Service Charge
                    </td>

                    <td class="dashboard__payment-history-table-data">
                      {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                      {{
                        new Intl.NumberFormat().format(
                          Number(invoice.service_charge)
                        )
                      }}
                    </td>
                  </tr>
                  <tr
                    v-for="(extraCharge, index) in extrachargeInvoice"
                    :key="index"
                  >
                    <td class="dashboard__payment-history-table-data">
                      {{ extraCharge.extrachargeType }}
                    </td>

                    <td class="dashboard__payment-history-table-data">
                      {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                      {{ new Intl.NumberFormat().format(extraCharge.amount) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="dashboard__payment-history-table-data">
                      Discount
                    </td>

                    <td class="dashboard__payment-history-table-data">
                      {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                      {{
                        new Intl.NumberFormat().format(Number(invoice.discount))
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td class="dashboard__payment-history-table-data">
                      VAT (7.5%)
                    </td>

                    <td class="dashboard__payment-history-table-data">
                      {{ invoice.currency === "GHS" ? "¢" : "₦" }}
                      {{ new Intl.NumberFormat().format(Number(invoice.vat)) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="invoice__total-wrap">
              <p class="invoice__total-title">
                Amount Due {{ invoice.currency === "GHS" ? "GHC" : "NGN" }}
              </p>
              <p class="invoice__total">
                {{ new Intl.NumberFormat().format(invoice.total) }}
              </p>
            </div>
            <p class="invoice__due">
              Next Due Date: {{ invoice.next_renewal_date | formatDate }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAllPaymentHistory,
  getPaymentInvoice,
  getAllExportPayment
} from "../../../services/invoice";
import { formatReadbleDate } from "../../utils/index";
import moment from "moment";
import AppModal from "../resuables/modal";
import loader from "../resuables/loader";
import html2pdf from "html2pdf.js";
export default {
  name: "invoices",
  components: {
    loader,
    AppModal
  },
  data() {
    return {
      startDate: "2021-04-16",
      currentPage: 1,
      pageSize: 10,
      count: 0,
      pages: 0,
      paymentHistories: [],
      dateRange: {
        end: new Date(),
        start: new Date(new Date().setDate(new Date().getDate() - 30))
      },
      dragValue: null,
      loader: false,
      invoice: null,
      extrachargeInvoice: null,
      showInvoice: false,
      exportData: [],
      exportLoader: false
    };
  },
  watch: {
    dateRange(newValue) {
      if (newValue) {
        this.getAllPaymentHistory();
        this.getAllExportPayment();
      }
    }
  },
  methods: {
    toggleInvoice(id, subPlan) {
      this.pageLoader = true;
      getPaymentInvoice(id)
        .then(data => {
          this.pageLoader = false;
          if (data.statusCode === 200) {
            let invoice = {
              subPlan: subPlan
            };
            this.invoice = { ...invoice, ...data.data.subscriptionPayments };
            this.extrachargeInvoice = data.data.extraChargePayments;
            this.showInvoice = !this.showInvoice;
          }
        })
        .catch(err => {
          this.pageLoader = false;
          return err;
        });
    },
    getAllPaymentHistory() {
      this.loader = true;
      const payload = {
        startDate: moment(this.dateRange.start).format("YYYY-MM-DD"),
        endDate: moment(this.dateRange.end).format("YYYY-MM-DD"),
        page: this.pages,
        limit: this.pageSize
      };
      getAllPaymentHistory(payload)
        .then(res => {
          this.loader = false;
          if (res.statusCode === 200) {
            this.paymentHistories = res.data.paymentHistory;
            this.count = res.data.totalPayments;
          }
        })
        .catch(err => {
          this.loader = false;
          return err;
        });
    },
    handlePagination(value) {
      if (value === 1) {
        this.pages = 0;
      } else {
        this.pages = (value - 1) * this.pageSize;
      }
      this.currentPage = value;
      this.getAllPaymentHistory();
    },
    formatDate(date) {
      return formatReadbleDate(date);
    },
    generateReport(invoice) {
      this.pageLoader = true;
      getPaymentInvoice(invoice.invoiceId)
        .then(data => {
          this.pageLoader = false;
          if (data.statusCode === 200) {
            let subPlan = {
              subPlan: invoice.subPlan
            };
            this.invoice = { ...subPlan, ...data.data.subscriptionPayments };
            this.extrachargeInvoice = data.data.extraChargePayments;
            if (this.invoice) {
              html2pdf(this.$refs.invoicePDF, {
                margin: 0,
                filename: `${this.invoice.invoiceNumber}-${
                  invoice.subPlan
                }-${moment(invoice.paymentDate).format("DD-MMM-YYYY")}-Inv.pdf`,
                image: { type: "jpeg", quality: 2.3 },
                html2canvas: { scale: 4, scrollX: 0, scrollY: 0 },
                jsPDF: { unit: "in", format: "letter", orientation: "portrait" }
              });
            }
          }
        })
        .catch(err => {
          this.pageLoader = false;
          return err;
        });
    },
    getAllExportPayment() {
      this.exportLoader = true;
      const payload = {
        startDate: moment(this.dateRange.start).format("YYYY-MM-DD"),
        endDate: moment(this.dateRange.end).format("YYYY-MM-DD")
      };
      getAllExportPayment(payload)
        .then(res => {
          this.exportLoader = false;
          if (res.statusCode === 200) {
            const items = [];
            res.data.paymentHistory.forEach(el => {
              for (var key in el) {
                if (
                  key === "securityDeposit" ||
                  key === "serviceCharge" ||
                  key === "rent" ||
                  key === "commission" ||
                  key === "bookingFee" ||
                  key === "discount" ||
                  key === "extraCharge"
                ) {
                  items.push({ ...el, key: key });
                }
              }
            });
            this.exportData = items.map(item => {
              return {
                ContactName: item.memberFullName,
                EmailAddress: item.memberEmail,
                InvoiceNumber: item.invoiceNumber,
                Reference: item.spaceName,
                InvoiceDate: this.formatDate(item.dueDate),
                DueDate: this.formatDate(item.dueDate),
                Total: item.amount,
                Description:
                  item.key === "serviceCharge"
                    ? "Service Charge"
                    : item.key === "securityDeposit"
                    ? "Security Deposit"
                    : item.key === "rent"
                    ? "Rental subscription"
                    : item.key == "commission"
                    ? "Commission"
                    : item.key === "bookingFee"
                    ? "Booking Fee"
                    : item.key === "extraCharge"
                    ? "Extra Charge"
                    : "",
                Quantity: 1,
                UnitAmount:
                  item.key === "serviceCharge"
                    ? item.serviceCharge
                    : item.key === "securityDeposit"
                    ? item.securityDeposit
                    : item.key === "rent"
                    ? Number(item.rent)
                    : item.key === "commission"
                    ? Number(item.commission)
                    : item.key === "bookingFee"
                    ? item.bookingFee
                    : item.key === "extraCharge"
                    ? item.extraCharge[0].total
                    : "",
                LineAmount:
                  item.key === "serviceCharge"
                    ? item.serviceCharge
                    : item.key === "securityDeposit"
                    ? item.securityDeposit
                    : item.key === "rent"
                    ? Number(item.rent)
                    : item.key === "commission"
                    ? Number(item.commission)
                    : item.key === "bookingFee"
                    ? item.bookingFee
                    : item.key === "extraCharge"
                    ? item.extraCharge[0].total
                    : "",
                TaxAmount:
                  item.key === "serviceCharge"
                    ? (7.5 / 100) * Number(item.serviceCharge)
                    : item.key === "securityDeposit"
                    ? 0
                    : item.key === "rent"
                    ? 0
                    : item.key === "commission"
                    ? 0
                    : item.key === "bookingFee"
                    ? (7.5 / 100) * Number(item.bookingFee)
                    : item.key === "extraCharge"
                    ? 0
                    : "",
                SubPlan: item.subPlan,
              };
            });
          }
        })
        .catch(err => {
          this.exportLoader = false;
          return err;
        });
    },
    formatInvoiceName(value) {
      if (!value) return "";
      return moment(value).format("YYYY-MM-DD");
    }
  },
  beforeMount() {
    this.getAllPaymentHistory();
    this.getAllExportPayment();
  },
  filters: {
    capitalize(value) {
      if (!value) return "";
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    formatNowDate(value) {
      if (!value) return "";
      if (value) {
        return moment(value).fromNow();
      }
    },
    capitalizeFirstLetter(value) {
      if (!value) return "";
      if (value) {
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
    },
    formatDate(value) {
      if (!value) return "";
      return moment(value).format("DD MMM. YYYY");
    }
  },
  computed: {
    selectDragAttribute() {
      return {
        popover: {
          visibility: "hover",
          isInteractive: false // Defaults to true when using slot
        }
      };
    },
    invoiceName() {
      if (this.dateRange) {
        return (
          this.formatInvoiceName(this.dateRange.start) +
          "_" +
          this.formatInvoiceName(this.dateRange.end)
        );
      } else {
        return "Last-30-days";
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.vc-appearance-none {
  border: none;
  height: 100%;
  width: 100%;
}
.renter_name {
  font-weight: 600;
  font-size: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/dashboard/invoices.vue